import Input from "../inputs/Input";
import { AiOutlineLoading } from "react-icons/ai";
import { Alert, Button, Label, Tabs } from "flowbite-react";
import useCEP from "../../hooks/useCEP";
import { Controller, useForm } from "react-hook-form";
import { ADMIN, USERS } from "../../config/apiConfig";
import axios from "../../config/axiosInstance";
import { useEffect, useMemo, useState } from "react";
import CPFInput from "../inputs/CPFInput";
import PhoneInput from "../inputs/PhoneInput";
import { HiInformationCircle, HiExternalLink } from "react-icons/hi";
import SelectInput from "../inputs/SelectInput";
import CurrencyInput from "../inputs/CurrencyInput";
import CNPJInput from "../inputs/CNPJInput";
import { FaUser } from "react-icons/fa6";
import { FaRegQuestionCircle } from "react-icons/fa";
import { IoMdPin } from "react-icons/io";
import { MdOutlineAttachMoney } from "react-icons/md";
import { IoSettingsSharp, IoLinkOutline, IoCopyOutline } from "react-icons/io5";
import { useQuery } from "react-query";
import { Tooltip } from "react-tooltip";

/**
 * CompanyEditForm
 * - Aba “Cadastro Online” (isOnline/tableId) com link /register/:uuid
 * - Prefill de isOnline/tableId a partir de company.representative
 * - Envio de representative no payload (hasOnlineRegister/tableId)
 * - Bloqueio de submit sem tableId quando isOnline = true
 */

const formatDateForInput = (value) => {
  if (!value) return "";
  // se já veio como YYYY-MM-DD, usa direto
  if (typeof value === "string") {
    const m = value.match(/^(\d{4}-\d{2}-\d{2})/);
    if (m) return m[1];
  }
  // tenta converter qualquer outra coisa (Date, número, string)
  try {
    const d = new Date(value);
    if (!isNaN(d)) return d.toISOString().slice(0, 10);
  } catch {}
  return "";
};

const Section = ({ title, description, children }) => (
  <div className="rounded-2xl border bg-white p-4 shadow-sm">
    <div className="mb-4">
      <h3 className="text-base md:text-lg font-semibold text-gray-900">
        {title}
      </h3>
      {description && (
        <p className="text-sm text-gray-500 mt-1">{description}</p>
      )}
    </div>
    <div className="grid grid-cols-1 gap-4">{children}</div>
  </div>
);

const GridRow = ({ children, cols = 2 }) => (
  <div className={`grid grid-cols-1 md:grid-cols-${cols} gap-4 md:gap-3`}>
    {children}
  </div>
);

const DAYS_OPTIONS = Array.from({ length: 30 }).map((_, i) => (
  <option key={i} value={i + 1}>
    {i + 1}
  </option>
));

const DEFAULTS = {
  uuid: "",
  status: 1,
  typeCustomer: "CNPJ",
  typePayment: "PRE",
  showLogo: true,
  showPriceQuery: false,
  generateAutomaticPayment: false,
  hourlyRequestLimit: 30,
  // novos (UI-only; enviados via representative):
  isOnline: false, // <- vira representative.hasOnlineRegister no PUT
  tableId: "", // <- vira representative.tableId (number/null) no PUT
};

const CompanyEditForm = ({ company, fetchCompanies, isOpen, onClose }) => {
  const [activeTab, setActiveTab] = useState(0);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // tabelas para cadastro online
  const [tables, setTables] = useState([]);
  const [loadingTables, setLoadingTables] = useState(false);
  const [tablesError, setTablesError] = useState(null);

  const {
    register,
    control,
    handleSubmit,
    reset,
    setValue,
    watch,
    unregister,
    setError: setFormError,
    formState: { errors },
  } = useForm({ defaultValues: DEFAULTS });

  const { isLoading: isLoadingUsers, data: users } = useQuery(
    ["usersAll", company],
    async () => {
      const { data } = await axios.get(
        `${ADMIN.GET_USERS}?itemsPerPage=${-1}&companyId=${
          company?.ParentCompany?.id
        }&status=1`
      );

      return (data?.data?.data || []).map((u) => ({
        id: u.id,
        name: u.name || u.email || `Usuário ${u.id}`,
      }));
    },
    { enabled: isOpen }
  );

  // CEP hook (usa campo `cep`)
  const { setCep, address } = useCEP();

  // ===== Map: company -> form (prioriza representative)
  const mapCompanyToForm = (c = {}) => {
    const rep = c?.representative || {};
    const initialIsOnline =
      typeof rep.hasOnlineRegister === "boolean"
        ? rep.hasOnlineRegister
        : !!c.isOnline;

    const rawTableId =
      rep.tableId != null ? rep.tableId : c.tableId != null ? c.tableId : "";

    const initialTableId = rawTableId === "" ? "" : String(rawTableId);

    return {
      ...DEFAULTS,
      ...c,
      uuid: c.uuid ?? "",
      cep: c.cep ?? c.addressPostalCode ?? "",
      address: c.address ?? c.street ?? "",
      addressNumber: c.addressNumber ?? "",
      addressComplement: c.addressComplement ?? "",
      addressNeighborhood: c.addressNeighborhood ?? c.neighborhood ?? "",
      addressCity: c.addressCity ?? c.city ?? "",
      addressState: c.addressState ?? c.state ?? "",
      responsibleCPF: c.responsibleCPF ?? c.responsibleDocument ?? "",
      position: c.position ?? c.role ?? "",
      vcm: c.vcm ?? "",
      limitUse: c.limitUse ?? "",
      minRechargePRE: c.minRechargePRE ?? "",
      paymentCreationDay: c.paymentCreationDay ?? "",
      paymentExpirationDay: c.paymentExpirationDay ?? "",
      hourlyRequestLimit:
        typeof c.hourlyRequestLimit === "number"
          ? c.hourlyRequestLimit
          : DEFAULTS.hourlyRequestLimit,
      showLogo: c.showLogo ?? DEFAULTS.showLogo,
      showPriceQuery: c.showPriceQuery ?? DEFAULTS.showPriceQuery,
      generateAutomaticPayment:
        c.generateAutomaticPayment ?? DEFAULTS.generateAutomaticPayment,
      typeCustomer: c.typeCustomer ?? DEFAULTS.typeCustomer,
      typePayment: c.typePayment ?? DEFAULTS.typePayment,
      responsibleId: c.responsibleId ?? "",
      status: typeof c.status === "number" ? c.status : DEFAULTS.status,
      asaas_id: c.asaas_id ?? c.asaasId ?? "",
      url: c?.representative?.url ?? "",
      dateEndContract: formatDateForInput(c?.dateEndContract),
      // cadastro online (UI)
      isOnline: initialIsOnline,
      tableId: initialTableId, // string para bater com <option value="...">
    };
  };

  useEffect(() => {
    if (!company) return;
    // sempre que a empresa chegar/mudar OU o modal abrir, reidrata os campos
    reset(mapCompanyToForm(company));
    setError(null);
    setValue("responsibleId", company?.responsibleId);
  }, [company, isOpen, reset, users]);

  // Carrega tabelas quando a aba "Cadastro Online" é ativada
  useEffect(() => {
    const shouldLoad = activeTab === 4;
    if (!shouldLoad) return;

    let aborted = false;
    (async () => {
      try {
        setLoadingTables(true);
        setTablesError(null);
        const TABLES_URL = ADMIN.GET_TABLES;
        const { data } = await axios.get(
          `${TABLES_URL}?companyId=${company?.id}`
        );
        const list = data?.data?.data ?? data?.data ?? [];
        if (!aborted) setTables(Array.isArray(list) ? list : []);
      } catch (e) {
        if (!aborted) {
          setTablesError("Não foi possível carregar as tabelas.");
          setTables([]);
        }
      } finally {
        if (!aborted) setLoadingTables(false);
      }
    })();

    return () => {
      aborted = true;
    };
  }, [activeTab, company?.id]);

  // Preenche campos de endereço vindos do CEP
  useEffect(() => {
    if (!address) return;
    setValue("address", address?.logradouro || "");
    setValue("addressNeighborhood", address?.bairro || "");
    setValue("addressCity", address?.localidade || "");
    setValue("addressState", address?.uf || "");
  }, [address, setValue]);

  const typeCustomer = watch("typeCustomer");
  const typePayment = watch("typePayment");
  const uuid = watch("uuid");
  const isOnline = watch("isOnline");
  const tableId = watch("tableId");

  useEffect(() => {
    if (typeCustomer !== "CNPJ") {
      unregister("responsibleCPF");
    }
  }, [typeCustomer, unregister]);

  const onSearchCEP = () => {
    const val = watch("cep");
    if (val) setCep(val);
  };

  const onSubmit = async (form) => {
    try {
      setSaving(true);
      const payload = { ...form };

      // Monta representative e remove campos de UI do root
      const representative = {
        hasOnlineRegister: !!payload.isOnline,
        tableId:
          payload.tableId === "" || payload.tableId == null
            ? null
            : Number(payload.tableId),
        url: payload.url,
      };
      delete payload.isOnline;
      delete payload.tableId;

      // Normalizações
      if (payload.vcm === "") delete payload.vcm;
      if (payload.limitUse === "") delete payload.limitUse;
      if (payload.minRechargePRE === "") delete payload.minRechargePRE;

      const body = {
        ...payload,
        representative, // <- backend faz upsert
      };

      await axios.put(`${ADMIN.GET_COMPANY}/${company?.id}`, body);

      onClose?.(false);
      fetchCompanies?.();
      setError(null);
    } catch (err) {
      console.error(err);
      setError(err);
    } finally {
      setSaving(false);
    }
  };

  const onlineRegisterUrl = useMemo(() => {
    if (!uuid) return "";
    const origin = company?.representative?.url
      ? company?.representative?.url
      : window.location.origin;
    return `${origin}/register/${uuid}`;
  }, [uuid]);

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      // fallback
      const el = document.createElement("textarea");
      el.value = text;
      document.body.appendChild(el);
      el.select();
      document.execCommand("copy");
      document.body.removeChild(el);
    }
  };

  const blockOnlineNoTable = isOnline && (tableId === "" || tableId == null);

  const initialTableIdFromCompany = useMemo(() => {
    const repId = company?.representative?.tableId;
    if (repId != null) return String(repId);
    if (company?.tableId != null) return String(company.tableId);
    return "";
  }, [company]);

  // quando as tables terminarem de carregar, re-aplica o valor se existir
  useEffect(() => {
    if (!initialTableIdFromCompany) return;
    if (loadingTables) return;

    const exists = tables.some(
      (t) => String(t.id) === initialTableIdFromCompany
    );
    if (exists) {
      setValue("tableId", initialTableIdFromCompany, {
        shouldValidate: false,
        shouldDirty: false,
      });
    }
  }, [initialTableIdFromCompany, loadingTables, tables, setValue]);

  return (
    <form className="relative" onSubmit={handleSubmit(onSubmit)}>
      {/* Abas */}
      <Tabs
        aria-label="Editar cliente"
        variant="underline"
        onActiveTabChange={setActiveTab}
        id="tab-company-details"
      >
        {/* 0 - CADASTRAL */}
        <Tabs.Item active title="Cadastral" icon={FaUser}>
          <div className="space-y-4">
            <Section
              title="Informações do cliente"
              description="Dados principais e identificação do cliente."
            >
              <GridRow cols={3}>
                <SelectInput
                  label="Status *"
                  name="status"
                  required
                  register={register}
                >
                  <option value={1}>Ativo</option>
                  <option value={0}>Bloqueado</option>
                  <option value={-1}>Inadimplente</option>
                </SelectInput>
                <SelectInput
                  label="Tipo de cliente *"
                  name="typeCustomer"
                  required
                  register={register}
                >
                  <option value="CPF">Pessoa física</option>
                  <option value="CNPJ">Pessoa jurídica</option>
                </SelectInput>
                <SelectInput
                  label="Tipo de pagamento *"
                  name="typePayment"
                  required
                  register={register}
                >
                  <option value="PRE">Pré-pago</option>
                  <option value="POS">Pós-pago</option>
                </SelectInput>
              </GridRow>

              {typeCustomer === "CPF" ? (
                <GridRow>
                  <Input
                    type="text"
                    label="Nome *"
                    name="name"
                    required
                    register={register}
                    error={errors?.name && "Este campo é obrigatório"}
                  />
                  <Controller
                    name="document"
                    control={control}
                    defaultValue=""
                    rules={{ required: true }}
                    render={({ field, fieldState }) => (
                      <CPFInput
                        label="CPF *"
                        name="document"
                        {...field} // fornece value/onChange/onBlur
                        error={fieldState.error && "Este campo é obrigatório"}
                      />
                    )}
                  />
                </GridRow>
              ) : (
                <>
                  <GridRow>
                    <Input
                      type="text"
                      label="Razão Social"
                      name="socialReason"
                      register={register}
                    />
                    <Controller
                      name="document"
                      control={control}
                      defaultValue=""
                      rules={{ required: true }}
                      render={({ field, fieldState }) => (
                        <CNPJInput
                          label="CNPJ *"
                          name="document"
                          {...field} // value, onChange, onBlur
                          error={fieldState.error && "Este campo é obrigatório"}
                        />
                      )}
                    />
                  </GridRow>
                  <GridRow>
                    <Input
                      type="text"
                      label="Nome do responsável"
                      name="responsibleName"
                      register={register}
                    />
                    <Controller
                      name="responsibleCPF"
                      control={control}
                      defaultValue=""
                      rules={{ required: true }}
                      render={({ field, fieldState }) => (
                        <CPFInput
                          label="CPF do responsável"
                          required
                          error={fieldState.error && "Este campo é obrigatório"}
                          {...field}
                        />
                      )}
                    />
                  </GridRow>
                  <GridRow cols={1}>
                    <SelectInput
                      label="Cargo"
                      name="position"
                      register={register}
                    >
                      <option value="SOCIO">Sócio/administrador</option>
                      <option value="GERENTE">Gerente</option>
                      <option value="ADM">Administrador</option>
                    </SelectInput>
                    <div />
                  </GridRow>
                </>
              )}

              <GridRow>
                <Controller
                  name="phoneNumber"
                  control={control}
                  defaultValue=""
                  rules={{ required: true }}
                  render={({ field, fieldState }) => (
                    <PhoneInput
                      label="Telefone *"
                      name="phoneNumber"
                      {...field} // value, onChange, onBlur
                      error={fieldState.error && "Este campo é obrigatório"}
                      mask="AUTO" // troca para "DEFAULT" se preferir fixo
                    />
                  )}
                />
                <Input
                  type="email"
                  label="E-mail"
                  name="email"
                  register={register}
                />
                {typeCustomer === "CNPJ" && (
                  <Input
                    type="email"
                    label="E-mail do financeiro"
                    name="financeEmail"
                    register={register}
                  />
                )}

                <div className="mt-1">
                  <SelectInput
                    register={register}
                    label="Vendedor responsável pelo cliente"
                    name="responsibleId"
                    disabled={isLoadingUsers}
                  >
                    <option value="">Todos os responsáveis</option>
                    {(users || []).map((u) => (
                      <option key={u.id} value={u.id}>
                        {u.name}
                      </option>
                    ))}
                  </SelectInput>
                </div>
                <div className="mt-1">
                  <Input
                    type="date"
                    register={register}
                    name="dateEndContract"
                    label="Término do contrato"
                  />
                </div>
              </GridRow>
            </Section>
          </div>
        </Tabs.Item>

        {/* 1 - ENDEREÇO */}
        <Tabs.Item title="Endereço" icon={IoMdPin}>
          <div className="space-y-6">
            <Section
              title="Localização"
              description="Preencha o CEP para buscar automaticamente logradouro, bairro, cidade e UF."
            >
              <GridRow cols={2}>
                <Input type="text" label="CEP" name="cep" register={register} />
                <Input
                  type="text"
                  label="Endereço"
                  name="address"
                  register={register}
                />
              </GridRow>
              <GridRow cols={3}>
                <Input
                  type="text"
                  label="Número"
                  name="addressNumber"
                  register={register}
                />
                <Input
                  type="text"
                  label="Complemento"
                  name="addressComplement"
                  register={register}
                />
                <Input
                  type="text"
                  label="Bairro"
                  name="addressNeighborhood"
                  register={register}
                />
              </GridRow>
              <GridRow cols={2}>
                <Input
                  type="text"
                  label="Cidade"
                  name="addressCity"
                  register={register}
                />
                <Input
                  type="text"
                  label="UF"
                  name="addressState"
                  register={register}
                />
              </GridRow>
            </Section>
          </div>
        </Tabs.Item>

        {/* 2 - FINANCEIRO */}
        <Tabs.Item title="Financeiro" icon={MdOutlineAttachMoney}>
          <div className="space-y-6">
            {typePayment === "POS" ? (
              <Section
                title="Condições do Pós-pago"
                description="Defina VCM, limite de uso e ciclo de faturamento."
              >
                <GridRow>
                  <CurrencyInput
                    control={control}
                    label="Consumação mínima (VCM)"
                    name="vcm"
                    register={register}
                  />
                  <CurrencyInput
                    control={control}
                    label="Valor máximo de uso"
                    name="limitUse"
                    register={register}
                  />
                </GridRow>
                <GridRow>
                  <SelectInput
                    label="Dia de criação do boleto"
                    name="paymentCreationDay"
                    register={register}
                  >
                    {DAYS_OPTIONS}
                  </SelectInput>
                  <SelectInput
                    label="Dia de vencimento do boleto"
                    name="paymentExpirationDay"
                    register={register}
                  >
                    {DAYS_OPTIONS}
                  </SelectInput>
                  <div className="mt-2">
                    <Input
                      type="number"
                      label={
                        <span className="flex items-center gap-2">
                          Qtd. máxima de requisições/hora
                          <FaRegQuestionCircle
                            data-tooltip-id="tt-hourly-limit"
                            className="text-gray-500"
                          />
                        </span>
                      }
                      name="hourlyRequestLimit"
                      register={register}
                      min={0}
                      max={1000}
                      registerOptions={{
                        valueAsNumber: true,
                        min: { value: 0, message: "Mínimo permitido é 0" },
                        max: {
                          value: 1000,
                          message: "Máximo permitido é 1000",
                        },
                        onChange: (e) => {
                          const num = Number(e.target.value);
                          if (num > 1000) e.target.value = 1000;
                          if (num < 0) e.target.value = 0;
                        },
                      }}
                      error={errors?.hourlyRequestLimit?.message}
                    />
                    <Tooltip
                      id="tt-hourly-limit"
                      content="Limite de requisições por hora para proteger contra uso abusivo e manter estabilidade."
                    />
                  </div>
                </GridRow>
                <label className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    {...register("generateAutomaticPayment")}
                    className="w-4 h-4"
                  />
                  <span>Gerar boleto automaticamente?</span>
                </label>
              </Section>
            ) : (
              <Section
                title="Condições do Pré-pago"
                description="Defina o valor mínimo de recarga para este cliente."
              >
                <CurrencyInput
                  control={control}
                  label="Valor mínimo de recarga"
                  name="minRechargePRE"
                  register={register}
                />
              </Section>
            )}

            <GridRow cols={1}>
              <Input
                type="text"
                label="ID do Asaas"
                name="asaas_id"
                register={register}
              />
              <div />
            </GridRow>
          </div>
        </Tabs.Item>

        {/* 3 - CONFIGURAÇÕES */}
        <Tabs.Item title="Configurações" icon={IoSettingsSharp}>
          <div className="space-y-6">
            <Section
              title="Preferências de exibição"
              description="Ajustes visuais nas consultas e documentos gerados."
            >
              <div className="flex flex-col gap-3">
                <label className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    {...register("showLogo")}
                    className="w-4 h-4"
                  />
                  <span>Mostrar logo nas consultas?</span>
                </label>
                <label className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    {...register("showPriceQuery")}
                    className="w-4 h-4"
                  />
                  <span>Mostrar preço nas consultas?</span>
                </label>

                <Input
                  type="text"
                  label="Url da Whitelabel"
                  name="url"
                  register={register}
                  className="mt-5"
                />
              </div>
            </Section>
          </div>
        </Tabs.Item>

        {/* 4 - CADASTRO ONLINE */}
        {company?.companyTypeId === "REPRESENTANTE" && (
          <Tabs.Item title="Cadastro Online" icon={IoLinkOutline}>
            <div className="space-y-6">
              <Section
                title="Acesso público para novos usuários"
                description="Ative o cadastro online e vincule uma tabela de preços. Compartilhe o link abaixo com os interessados."
              >
                <div className="flex items-center gap-3 mb-2">
                  <input
                    type="checkbox"
                    {...register("isOnline")}
                    className="w-4 h-4"
                    id="isOnline"
                  />
                  <Label htmlFor="isOnline">Ativar cadastro online?</Label>
                </div>

                <GridRow cols={1}>
                  <SelectInput
                    label="Tabela vinculada"
                    name="tableId"
                    register={register}
                    disabled={!isOnline}
                    required={false}
                  >
                    <option value="">— Selecione —</option>
                    {loadingTables && (
                      <option value="" disabled>
                        Carregando...
                      </option>
                    )}
                    {!loadingTables &&
                      tables.map((t) => (
                        <option key={t.id} value={t.id}>
                          {t.name}
                        </option>
                      ))}
                  </SelectInput>
                </GridRow>

                {isOnline && (
                  <div className="rounded-xl border border-slate-200 p-4 bg-white shadow-sm">
                    <div className="mb-1 flex items-center gap-2 text-sm font-medium text-slate-900">
                      <IoLinkOutline className="h-5 w-5" />
                      Link de cadastro
                    </div>
                    <p className="text-xs text-slate-500 mb-2">
                      Este é o link público para novos usuários se cadastrarem.
                    </p>

                    <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                      <div className="truncate">{onlineRegisterUrl}</div>
                      <div className="flex gap-2">
                        <Button
                          type="button"
                          color="light"
                          onClick={() =>
                            onlineRegisterUrl &&
                            copyToClipboard(onlineRegisterUrl)
                          }
                          disabled={!onlineRegisterUrl}
                          className="whitespace-nowrap"
                        >
                          <IoCopyOutline className="mr-2 h-4 w-4" />
                          Copiar link
                        </Button>
                      </div>
                    </div>

                    {!uuid && (
                      <Alert color="warning" className="mt-3">
                        Esta empresa ainda não possui <b>UUID</b>. Ele é gerado
                        automaticamente para empresas do tipo{" "}
                        <b>REPRESENTANTE</b>. Sem o UUID o link não é formado.
                      </Alert>
                    )}
                    {tablesError && (
                      <Alert color="failure" className="mt-3">
                        {tablesError}
                      </Alert>
                    )}
                  </div>
                )}
              </Section>
            </div>
          </Tabs.Item>
        )}
      </Tabs>

      {/* Rodapé fixo de ações */}
      <div className="sticky bottom-0 left-0 right-0 mt-6 border-t bg-white/90 backdrop-blur supports-[backdrop-filter]:bg-white/60">
        <div className="mx-auto max-w-6xl px-4 py-3 flex items-center justify-end gap-3">
          <Button
            color="light"
            onClick={() => onClose?.(false)}
            disabled={saving}
          >
            Cancelar
          </Button>
          <Button
            type="submit"
            className="primary"
            isProcessing={saving}
            processingSpinner={
              <AiOutlineLoading className="h-5 w-5 animate-spin" />
            }
            disabled={saving}
          >
            Salvar alterações
          </Button>
        </div>
      </div>

      {error && (
        <div className="mt-4">
          <Alert color="failure" icon={HiInformationCircle}>
            Erro ao atualizar cliente
          </Alert>
        </div>
      )}
    </form>
  );
};

export default CompanyEditForm;
